import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	TFileId,
	TLanguageIDs,
	IItemForGetShortInfo,
	IDefaultLanguage,
	IUpdateSubcategoryV2,
	IGetSubcategoryInfoV2,
} from 'types';
import { editSubcategoryInitialState, EditSubcategoryContext, TTypeOfModalWindow } from './editSubcategoryContext';
import { editCategoryReducer } from './editSubcategoryReducer';
import { EditSubcategoryActionTypes } from './editSubcategoryActions';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getSubcategoryInfoV2 } from 'backend/subcategories';

export const EditSubcategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editCategoryReducer, editSubcategoryInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const subcategoryID = params.get('subcategoryID');

	const { response, loading, refetch } = useFetchData<IGetSubcategoryInfoV2, string>(
		subcategoryID ? getSubcategoryInfoV2 : null,
		subcategoryID || '',
	);

	useNamedHeader(response?.data.name);

	useEffect(() => {
		if (response) {
			const translations = response.data.translations.map((translation) => ({
				languageId: translation.languageId,
				name: translation.name,
			}));

			dispatch({
				type: EditSubcategoryActionTypes.SET_ALL_DATA,
				payload: {
					subcategoryID: subcategoryID || '',
					filePath: response.data.photoPath,
					initialIDSelectedCustomer: response.data.customer.id,
					initialIDsSelectedRestaurant: response.data.restaurants.map((restaurant) => restaurant.id),
					nameOfSubcategory: response.data.name || '',
					initialIDSelectedCategory: response.data.category.id,
					hasSchedulePriority: response.data.overrideScheduleAvailability,
					activationSchedule: response.data.activationCron || '',
					deactivationSchedule: response.data.deactivationCron || '',
					translations,
				},
			});
		}
	}, [subcategoryID, response]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditSubcategoryActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditSubcategoryActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeLoaderStatus = useCallback((value: boolean) => {
		dispatch({
			type: EditSubcategoryActionTypes.CHANGE_LOADER_STATUS,
			payload: {
				isLoading: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditSubcategoryActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditSubcategoryActionTypes.DELETE_PHOTO }), []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditSubcategoryActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditSubcategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditSubcategoryActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback((value: string[]) => {
		dispatch({
			type: EditSubcategoryActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const changeNameOfSubcategory = useCallback((value: string) => {
		dispatch({
			type: EditSubcategoryActionTypes.CHANGE_NAME_OF_SUBCATEGORY,
			payload: {
				nameOfSubcategory: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditSubcategoryActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				fetchedCategories: value,
			},
		});
	}, []);

	const setIDCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditSubcategoryActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	}, []);

	const toggleHasSchedulePriority = useCallback(() => {
		dispatch({
			type: EditSubcategoryActionTypes.TOGGLE_HAS_SCHEDULE_PRIORITY,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditSubcategoryActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditSubcategoryActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditSubcategoryActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditSubcategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditSubcategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateSubcategoryV2>) => {
		dispatch({
			type: EditSubcategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditSubcategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<EditSubcategoryContext.Provider
			value={{
				refetchData,
				changeLoaderStatus,
				setFileId,
				deletePhoto,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				changeNameOfSubcategory,
				setFetchedCategories,
				setIDCategory,
				toggleHasSchedulePriority,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				isLoading: state.isLoading,
				filePath: state.filePath,
				fileId: state.fileId,
				subcategoryID: state.subcategoryID,
				fetchedCustomers: state.fetchedCustomers,
				initialIDSelectedCustomer: state.initialIDSelectedCustomer,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				initialIDsSelectedRestaurant: state.initialIDsSelectedRestaurant,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				nameOfSubcategory: state.nameOfSubcategory,
				fetchedCategories: state.fetchedCategories,
				initialIDSelectedCategory: state.initialIDSelectedCategory,
				IDSelectedCategory: state.IDSelectedCategory,
				hasSchedulePriority: state.hasSchedulePriority,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditSubcategoryContext.Provider>
	);
};
